import {ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {UserGroupQuery} from '../../../queries/user-group.query';
import {UserGroupService} from '../../../services/user-group/user-group.service';
import {SnackbarService} from '../../../services/snackbar/snackbar.service';
import {VaultQuery} from '../../../queries/vault.query';
import {UserListsService} from '../../../services/lists/user-lists.service';
import {VaultMemberStatus} from '../../../models/vault-member-status.model';
import {ApteraApiError} from '../../../models/aptera-api-error.model';
import {UNASSIGNED_USER_GROUP_ID} from '../../../constants/user-groups.constant';
import {IconsComponent} from '../../dummy-components/icons.component';
import {HttpErrorResponse} from '@angular/common/http';
import {DocumentFilenameComponent} from '../../document-filename/document-filename.component';
import {KebabCasePipe} from '../../../pipes/kebab-case/kebab-case.pipe';
import {TranslateModule} from '@ngx-translate/core';
import {AuthImagePipe} from '../../../pipes/auth-image.pipe';
import {TooltipDirective} from '../../../directives/tooltip/tooltip.directive';
import {TooltipPositionEnum} from '../../../enums/tooltip/tooltip-position.enum';
import {ApiIconUrlPipe} from '../../../pipes/api-icon-url/api-icon-url.pipe';
import {ButtonComponent} from '../../buttons/button/button.component';
import {AppButtonTypeEnum} from '../../../enums/button/app-button-type.enum';
import {ButtonColorEnum} from '../../../enums/button/button-color.enum';
import {IconComponent} from '../../icon/icon.component';
import {AppIconSizeEnum} from '../../../enums/app-icon-size.enum';
import {APP_MODAL_REF} from '../../../constants/modal/app-modal-ref.constant';
import {AppDialogModal} from '../../../models/modal/dialog/app-dialog-modal.model';
import {AppAddUserToGroupStatusDialogData} from '../../../types/modal/dialog/app-add-user-to-group-status-dialog-data.type';
import {ModalService} from '../../../services/modal/modal.service';
import {VaultMemberAssignStatusEnum} from '../../../enums/vault-member-assign-status.enum';

@Component({
    selector: 'app-add-user-to-group-status-dialog',
    templateUrl: './add-user-to-group-status-dialog.component.html',
    styleUrls: ['./add-user-to-group-status-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DocumentFilenameComponent,
        KebabCasePipe,
        TranslateModule,
        AuthImagePipe,
        TooltipDirective,
        ApiIconUrlPipe,
        ButtonComponent,
        IconComponent
    ]
})
export class AddUserToGroupStatusDialogComponent extends IconsComponent implements OnInit {
    public readonly modalData: AppAddUserToGroupStatusDialogData;
    protected vaultMemberQueue: WritableSignal<Array<VaultMemberStatus>>;
    protected vaultMembersAreInProgress: WritableSignal<boolean>;
    protected aborted: WritableSignal<boolean>;
    protected readonly qaTag: string;
    protected readonly TooltipPositionEnum = TooltipPositionEnum;
    protected readonly AppIconSize = AppIconSizeEnum;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected readonly ButtonColorEnum = ButtonColorEnum;
    protected readonly VaultMemberAssignStatusEnum = VaultMemberAssignStatusEnum;
    private readonly userGroupQuery = inject(UserGroupQuery);
    private readonly userGroupService = inject(UserGroupService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly userListService = inject(UserListsService);
    private readonly modalService = inject(ModalService);
    private readonly modalRef = inject<AppDialogModal<AddUserToGroupStatusDialogComponent, AppAddUserToGroupStatusDialogData, void>>(APP_MODAL_REF);

    public constructor() {
        super();
        this.qaTag = this.modalRef.qaTag;
        this.modalData = this.modalRef.modalData;
        this.vaultMemberQueue = signal([]);
        this.vaultMembersAreInProgress = signal(true);
        this.aborted = signal(false);
    }

    public ngOnInit(): void {
        this.addSelectedUsersToUserGroup()
            .then();
    }

    protected abort(): void {
        this.aborted.set(true);
    }

    protected close(): void {
        this.modalService.closeModal(this.modalRef);
    }

    private async addSelectedUsersToUserGroup(): Promise<void> {
        const firstActiveId = this.userGroupQuery.getFirstActiveId();
        const userGroupId = (!firstActiveId || firstActiveId === 'undefined') ? UNASSIGNED_USER_GROUP_ID : firstActiveId;

        this.vaultMemberQueue.set(this.modalData.selectedUsers.map(user => {
            return {
                status: VaultMemberAssignStatusEnum.Waiting,
                error: null,
                vaultMember: user,
            };
        }));

        for (const [vaultMemberIndex, vaultMember] of this.modalData.selectedUsers.entries()) {
            this.updateVaultMemberStatus(vaultMemberIndex, VaultMemberAssignStatusEnum.InProgress, null);

            try {
                if (userGroupId === UNASSIGNED_USER_GROUP_ID) {
                    await this.userGroupService.addUserToUnassignedVaultUserGroup(vaultMember.userId);
                } else {
                    await this.userGroupService.addUserToVaultUserGroup(userGroupId, vaultMember.userId);
                }

                this.updateVaultMemberStatus(vaultMemberIndex, VaultMemberAssignStatusEnum.Done, null);
            } catch (error) {
                const httpError = error as HttpErrorResponse;
                this.updateVaultMemberStatus(vaultMemberIndex, VaultMemberAssignStatusEnum.Error, httpError.error || httpError);
            }

            if (this.aborted()) {
                this.snackbarService.showInfo('VAULT_ADMIN.OPERATION_ABORTED');
                this.vaultMembersAreInProgress.set(false);

                return;
            }
        }

        this.vaultMembersAreInProgress.set(false);

        if (this.vaultMemberQueue()
            .filter(vaultMember => vaultMember.status === VaultMemberAssignStatusEnum.Done).length > 0) {
            const vaultId = this.vaultQuery.getFirstActiveVaultId();

            if (vaultId) {
                this.userListService.getUserGroupMemberList(vaultId, userGroupId)
                    ?.setRefreshListToast(true);
            }

            this.snackbarService.showInfo('VAULT_ADMIN.USER_GROUP_UPDATED_MESSAGE');
        }
    }

    private updateVaultMemberStatus(vaultMemberIndex: number,
                                    status: VaultMemberAssignStatusEnum,
                                    error: ApteraApiError | null): void {
        this.vaultMemberQueue.update(vaultMemberQueue => vaultMemberQueue.map((vaultMemberStatus, currentIndex) => {
            return (currentIndex === vaultMemberIndex) ? {
                ...vaultMemberStatus,
                status,
                error
            } : vaultMemberStatus;
        }));
    }
}
